import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, BellRing, Percent, Undo2, Wallet, XCircle } from 'lucide-react';
import { MANUAL_METHODS, PAYMENT_METHOD_LABEL, ATTEMPT_STATUS_LABEL, parseBRL, type Permission } from '@garlic/shared';
import { api, errorMessage, post } from '../../lib/api';
import { formatBRL, formatDateTime } from '../../lib/format';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Field, Input, PageHeader, Select, Spinner, cx } from '../../ui/primitives';
import { Dialog, ReasonDialog, useToast } from '../../ui/feedback';
import { MoneyInput } from '../../ui/controls';
import type { TenantRestaurant } from '../TenantApp';
import { useTables } from './StaffTables';
import { ItemUnitsPicker, selectionCents, selectionPayload, type PayableItem, type UnitSelection } from '../ItemUnitsPicker';

type Item = PayableItem & { orderNumber: number };
type StaffAccount = {
  session: { id: string; number: number; status: string; tableLabel: string; billRequestedAt: string | null };
  account: {
    consumedCents: number; discountCents: number; paidCents: number; processingCents: number; dueCents: number; availableCents: number; overpaidCents: number; settled: boolean;
    items: Item[];
    discounts: { id: string; amountCents: number; reason: string; createdAt: string }[];
    payments: { id: string; kind: string; method: string; amountCents: number; status: string; createdAt: string; note?: string | null; reversalReason?: string | null }[];
    processing: { id: string; method: string; amountCents: number; status: string }[];
  };
  attempts: { id: string; method: string; amountCents: number; status: string; createdAt: string; detail: string | null; error: string | null }[];
  onlineAvailable: boolean;
};

export function StaffAccounts({ restaurant, permissions }: { restaurant: TenantRestaurant; permissions: Permission[] }) {
  const tables = useTables();
  const [selected, setSelected] = useState<string | null>(null);
  const open = (tables.data ?? []).filter((t) => t.session);
  const current = selected ?? open[0]?.session?.id ?? null;
  return (
    <>
      <PageHeader title="Contas e caixa" description="Contas abertas, pagamentos e encerramento. Valores atualizam em tempo real." />
      {tables.isPending && <Spinner />}
      {tables.data && open.length === 0 && <Card><EmptyState icon={<Wallet className="size-5" />} title="Nenhuma conta aberta" /></Card>}
      {open.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[17rem_1fr]">
          <Card className="self-start">
            <ul className="divide-y divide-line">
              {open.map((t) => {
                const s = t.session!;
                return (
                  <li key={s.id}>
                    <button onClick={() => setSelected(s.id)} className={cx('flex w-full items-center gap-3 px-4 py-3 text-left text-sm', current === s.id ? 'bg-paper-2' : 'hover:bg-paper')}>
                      <span className="font-display text-xl font-semibold">{t.label}</span>
                      <span className="min-w-0 flex-1 text-xs text-ink-3">
                        Comanda {s.number}
                        {s.billRequestedAt && <span className="ml-1 inline-flex items-center gap-0.5 font-medium text-warn-600"><BellRing className="size-3" /> conta</span>}
                      </span>
                      <span className="text-right">
                        <span className={cx('block font-semibold tabular-nums', s.settled && 'text-sprout-600')}>{formatBRL(s.dueCents ?? s.consumedCents)}</span>
                        <span className="block text-[11px] text-ink-3">{s.settled ? 'paga' : s.processingCents ? 'processando' : 'a pagar'}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
          {current && <AccountDetail key={current} sessionId={current} restaurant={restaurant} permissions={permissions} />}
        </div>
      )}
    </>
  );
}

function AccountDetail({ sessionId, restaurant, permissions }: { sessionId: string; restaurant: TenantRestaurant; permissions: Permission[] }) {
  const can = (p: Permission) => permissions.includes(p);
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['staff-account', sessionId], queryFn: () => api<StaffAccount>(`/api/staff/sessions/${sessionId}/account`), refetchInterval: 20_000 });
  const [manual, setManual] = useState(false);
  const [discount, setDiscount] = useState(false);
  const [reverse, setReverse] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['staff-account', sessionId] });
    await qc.invalidateQueries({ queryKey: ['staff-tables'] });
  };
  if (q.isPending) return <Spinner />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const { session, account: a, attempts } = q.data;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title={`Mesa ${session.tableLabel} · comanda ${session.number}`}
          description={a.settled ? 'Conta quitada' : a.processingCents ? 'Há pagamento em processamento' : undefined}
          action={
            <div className="flex flex-wrap gap-2">
              {can('payments.manual.create') && !a.settled && <Button icon={<Wallet className="size-4" />} onClick={() => setManual(true)} disabled={a.availableCents <= 0}>Registrar pagamento</Button>}
              {can('discounts.apply') && !a.settled && <Button variant="secondary" icon={<Percent className="size-4" />} onClick={() => setDiscount(true)} disabled={a.availableCents <= 0}>Desconto</Button>}
              {can('sessions.close') && <Button variant={a.settled ? 'primary' : 'secondary'} icon={<XCircle className="size-4" />} onClick={() => setClosing(true)}>Encerrar comanda</Button>}
            </div>
          }
        />
        <div className="grid grid-cols-2 gap-px bg-line sm:grid-cols-4">
          {[
            ['Consumido', a.consumedCents],
            ['Descontos', a.discountCents],
            ['Pago', a.paidCents],
            ['Falta', a.dueCents],
          ].map(([l, v]) => (
            <div key={l as string} className="bg-white px-5 py-3">
              <p className="text-xs text-ink-3">{l}</p>
              <p className={cx('font-display text-2xl font-semibold tabular-nums', l === 'Falta' && a.settled && 'text-sprout-600')}>{formatBRL(v as number)}</p>
            </div>
          ))}
        </div>
        {a.processingCents > 0 && <div className="border-t border-line px-5 py-3"><Alert tone="warning">{formatBRL(a.processingCents)} em processamento no Mercado Pago. Não cobre esse valor de novo.</Alert></div>}
        {a.overpaidCents > 0 && <div className="border-t border-line px-5 py-3"><Alert tone="danger" title="Recebido a mais">{formatBRL(a.overpaidCents)} acima do devido. Faça o estorno no Mercado Pago.</Alert></div>}
      </Card>

      <Card>
        <CardHeader title="Itens" />
        <ul className="divide-y divide-line text-sm">
          {a.items.map((i) => (
            <li key={i.id} className={cx('flex items-center justify-between gap-3 px-5 py-2.5', i.cancelled && 'text-ink-3 line-through')}>
              <span>#{String(i.orderNumber).padStart(4, '0')} · {i.quantity}× {i.productName}{i.options.length > 0 && <span className="text-xs text-ink-3"> ({i.options.join(', ')})</span>}</span>
              <span className="flex items-center gap-2">
                {i.paidCents > 0 && <Badge tone="success">pago</Badge>}
                {i.reservedCents > 0 && <Badge tone="warning">em pagamento</Badge>}
                <span className="tabular-nums">{formatBRL(i.totalCents)}</span>
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <CardHeader title="Pagamentos e descontos" />
        {a.payments.length === 0 && a.discounts.length === 0 ? <p className="px-5 py-4 text-sm text-ink-3">Nenhum ainda.</p> : (
          <ul className="divide-y divide-line text-sm">
            {a.payments.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5">
                <span>
                  {PAYMENT_METHOD_LABEL[p.method] ?? p.method}
                  <Badge tone={p.kind === 'DIGITAL' ? 'brand' : 'neutral'}>{p.kind === 'DIGITAL' ? 'confirmado pelo Mercado Pago' : 'registro manual'}</Badge>
                  <span className="block text-xs text-ink-3">{formatDateTime(p.createdAt, restaurant.timezone)}{p.note ? ` · ${p.note}` : ''}{p.reversalReason ? ` · estornado: ${p.reversalReason}` : ''}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className={cx('tabular-nums font-medium', p.status === 'REVERSED' && 'text-ink-3 line-through')}>{formatBRL(p.amountCents)}</span>
                  {p.kind === 'MANUAL' && p.status === 'CONFIRMED' && can('payments.refund') && session.status === 'OPEN' && (
                    <Button size="sm" variant="ghost" aria-label="Estornar registro" icon={<Undo2 className="size-4" />} onClick={() => setReverse(p.id)} />
                  )}
                </span>
              </li>
            ))}
            {a.discounts.map((d) => (
              <li key={d.id} className="flex justify-between gap-2 px-5 py-2.5">
                <span>Desconto <span className="block text-xs text-ink-3">{d.reason}</span></span>
                <span className="tabular-nums">− {formatBRL(d.amountCents)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {attempts.length > 0 && (
        <Card>
          <CardHeader title="Tentativas pelo celular" description="Histórico de Pix e cartão desta comanda." />
          <ul className="divide-y divide-line text-sm">
            {attempts.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5">
                <span>{t.method === 'PIX' ? 'Pix' : 'Cartão'} · {formatBRL(t.amountCents)}<span className="block text-xs text-ink-3">{formatDateTime(t.createdAt, restaurant.timezone)}{t.detail ? ` · ${t.detail}` : ''}{t.error ? ` · ${t.error}` : ''}</span></span>
                <span className="flex items-center gap-2">
                  <Badge tone={t.status === 'APPROVED' ? 'success' : t.status === 'PENDING' || t.status === 'CREATED' ? 'warning' : 'neutral'}>{ATTEMPT_STATUS_LABEL[t.status] ?? t.status}</Badge>
                  {(t.status === 'PENDING' || t.status === 'CREATED') && can('payments.manual.create') && (
                    <Button size="sm" variant="ghost" icon={<Ban className="size-4" />} onClick={async () => {
                      if (!confirm('Encerrar esta tentativa? O Garlic consulta o Mercado Pago antes: se já estiver paga, fica registrada como paga.')) return;
                      try { await post(`/api/staff/payment-attempts/${t.id}/cancel`); await refresh(); toast.success('Tentativa encerrada'); } catch (e) { toast.error(errorMessage(e)); }
                    }}>Encerrar</Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {manual && <ManualPaymentDialog sessionId={sessionId} account={a} onClose={() => setManual(false)} onDone={refresh} />}
      {discount && <DiscountDialog sessionId={sessionId} max={a.availableCents} onClose={() => setDiscount(false)} onDone={refresh} />}
      <ReasonDialog
        open={!!reverse}
        onClose={() => setReverse(null)}
        title="Estornar registro manual"
        description="Use quando o pagamento foi lançado por engano. O valor volta a ser devido."
        confirmLabel="Estornar"
        tone="danger"
        onConfirm={async (reason) => {
          try {
            await post(`/api/staff/payments/${reverse}/reverse`, { reason });
            await refresh();
            toast.success('Registro estornado');
          } catch (e) {
            throw new Error(errorMessage(e));
          }
        }}
      />
      <ReasonDialog
        open={closing}
        onClose={() => setClosing(false)}
        title={`Encerrar comanda ${session.number}`}
        description={a.settled || a.consumedCents === 0 ? 'A conta está quitada. Informe uma observação para encerrar (ex.: "cliente saiu").' : <>Ainda faltam <strong>{formatBRL(a.dueCents)}</strong>. Encerrar assim é uma exceção registrada na auditoria e exige permissão especial.</>}
        confirmLabel="Encerrar"
        tone={a.settled ? 'primary' : 'danger'}
        onConfirm={async (reason) => {
          try {
            await post(`/api/staff/sessions/${sessionId}/close`, { reason });
            await refresh();
            toast.success('Comanda encerrada');
          } catch (e) {
            throw new Error(errorMessage(e));
          }
        }}
      />
    </div>
  );
}

function DiscountDialog({ sessionId, max, onClose, onDone }: { sessionId: string; max: number; onClose: () => void; onDone: () => Promise<void> }) {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const cents = parseBRL(amount) ?? 0;
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await post(`/api/staff/sessions/${sessionId}/discounts`, { amountCents: cents, reason });
      await onDone();
      toast.success('Desconto aplicado');
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title="Aplicar desconto" footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button loading={busy} disabled={cents <= 0 || cents > max || reason.trim().length < 5} onClick={submit}>Aplicar</Button></>}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label={`Valor (máximo ${formatBRL(max)})`}>{(id) => <MoneyInput id={id} value={amount} onChange={setAmount} invalid={cents > max} />}</Field>
        <Field label="Motivo (fica na auditoria)">{(id) => <Input id={id} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: demora no atendimento" maxLength={300} />}</Field>
      </div>
    </Dialog>
  );
}

function ManualPaymentDialog({ sessionId, account: a, onClose, onDone }: { sessionId: string; account: StaffAccount['account']; onClose: () => void; onDone: () => Promise<void> }) {
  const toast = useToast();
  const [method, setMethod] = useState<(typeof MANUAL_METHODS)[number]>('CASH');
  const [mode, setMode] = useState<'TOTAL' | 'AMOUNT' | 'ITEMS'>('TOTAL');
  const [amount, setAmount] = useState('');
  const [items, setItems] = useState<UnitSelection>({});
  const [note, setNote] = useState('');
  const [received, setReceived] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const payable = a.items.filter((i) => !i.cancelled && i.remainingCents > 0);
  const value = mode === 'TOTAL' ? a.availableCents : mode === 'AMOUNT' ? parseBRL(amount) ?? 0 : selectionCents(payable, items);
  const receivedCents = parseBRL(received) ?? 0;
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await post(`/api/staff/sessions/${sessionId}/payments`, { method, mode, ...(mode === 'AMOUNT' ? { amountCents: value } : {}), ...(mode === 'ITEMS' ? { itemUnits: selectionPayload(items) } : {}), note: note || undefined });
      await onDone();
      toast.success(`${formatBRL(value)} registrado`);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title="Registrar pagamento presencial" footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button loading={busy} disabled={value <= 0 || value > a.availableCents} onClick={submit}>Registrar {formatBRL(Math.max(0, value))}</Button></>}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label="Meio de pagamento">{(id) => <Select id={id} value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>{MANUAL_METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</option>)}</Select>}</Field>
        {method === 'PIX_MANUAL' && <Alert tone="warning">Confira no app do banco que o Pix entrou. Este registro é manual: o Garlic não confirma o recebimento automaticamente.</Alert>}
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-paper-2 p-1 text-sm">
          {([['TOTAL', `Tudo (${formatBRL(a.availableCents)})`], ['AMOUNT', 'Valor'], ['ITEMS', 'Itens']] as const).map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} className={cx('rounded-lg py-1.5 font-medium', mode === k ? 'bg-white shadow-sm' : 'text-ink-3')}>{l}</button>
          ))}
        </div>
        {mode === 'AMOUNT' && <Field label="Valor recebido">{(id) => <MoneyInput id={id} value={amount} onChange={setAmount} />}</Field>}
        {mode === 'ITEMS' && <ItemUnitsPicker className="max-h-72 overflow-y-auto" items={payable} value={items} onChange={setItems} accentClass="accent-garlic-600" />}
        {method === 'CASH' && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cliente entregou (opcional)">{(id) => <MoneyInput id={id} value={received} onChange={setReceived} />}</Field>
            <div className="self-end rounded-xl bg-paper-2 px-3 py-2.5 text-sm">Troco: <strong className="tabular-nums">{formatBRL(Math.max(0, receivedCents - value))}</strong></div>
          </div>
        )}
        <Field label="Observação (opcional)">{(id) => <Input id={id} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />}</Field>
      </div>
    </Dialog>
  );
}
